import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import constants from '../../constants';

// GH-841 (GH-836): "הצעות נושאים" — what members wrote in the suggestion box on the circles list page
// ("איזה נושא הייתם רוצים שנוסיף למעגלי התמיכה?"). Newest first, 50 per page.

interface Suggestion {
	_id: string;
	text: string;
	createdAt: string;
	member?: { _id: string; first_name?: string; last_name?: string; email?: string } | null;
	company?: { _id: string; english_name?: string } | null;
}

const API = `${constants.API_URL}/support-circles/admin/suggestions`;
const LIMIT = 50;

export default function SupportCircleSuggestionsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
	const notify = useNotify();
	const [page, setPage] = useState(0); // 0-based for TablePagination; the server is 1-based
	const [docs, setDocs] = useState<Suggestion[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true); // true until the first load, so the empty row doesn't flash on open

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const res = await axios.get(`${API}?page=${page + 1}&limit=${LIMIT}`);
			setDocs(res.data?.docs || []);
			setTotal(res.data?.totalDocs || 0);
		} catch (e: any) {
			notify(e?.response?.data?.message || 'טעינת ההצעות נכשלה', { type: 'error' });
		} finally {
			setLoading(false);
		}
	}, [page, notify]);
	useEffect(() => {
		if (open) load();
	}, [open, load]);
	// reopening starts again from the newest page
	const close = () => {
		setPage(0);
		onClose();
	};

	return (
		<Dialog open={open} onClose={close} maxWidth="md" fullWidth dir="rtl">
			<DialogTitle>הצעות נושאים{total ? ` (${total})` : ''}</DialogTitle>
			<DialogContent>
				<Typography variant="caption" color="text.secondary">
					מה שמנויים כתבו בתיבת ההצעות בעמוד מעגלי התמיכה. נראה רק לצוות GroupHug.
				</Typography>
				<TableContainer sx={{ mt: 1 }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell align="right">תאריך</TableCell>
								<TableCell align="right">הצעה</TableCell>
								<TableCell align="right">חברה</TableCell>
								<TableCell align="right">מנוי/ה</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{docs.map((s) => (
								<TableRow key={s._id} hover>
									<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{moment(s.createdAt).format('DD/MM/YYYY HH:mm')}</TableCell>
									<TableCell align="right" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{s.text}</TableCell>
									<TableCell align="right">{s.company?.english_name || '—'}</TableCell>
									<TableCell align="right">{`${s.member?.first_name || ''} ${s.member?.last_name || ''}`.trim() || '—'}</TableCell>
								</TableRow>
							))}
							{loading && docs.length === 0 && (
								<TableRow>
									<TableCell colSpan={4} align="center">
										<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>טוען…</Typography>
									</TableCell>
								</TableRow>
							)}
							{!loading && docs.length === 0 && (
								<TableRow>
									<TableCell colSpan={4} align="center">
										<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>אין הצעות עדיין.</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				{total > LIMIT && (
					<TablePagination
						component="div"
						count={total}
						page={page}
						onPageChange={(_, p) => setPage(p)}
						rowsPerPage={LIMIT}
						rowsPerPageOptions={[]}
						labelDisplayedRows={({ from, to, count }) => `${from}-${to} מתוך ${count}`}
					/>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={close}>סגירה</Button>
			</DialogActions>
		</Dialog>
	);
}
